/**
 * Fab recipes (M8 plan MC-c1a, D-TBD T23): one pure lookup over each Mk II/III module's `materials` block
 * (`data/modules.json`, schema `fabMaterialsSchema` in `m2Schemas.ts`). Player and AI share this one table (plan §2).
 * MC-c6 adds `offeredAt` (reading `where`; Mk III geography, plan decisions 4 and 5); MC-f2c adds pattern recipes (the
 * base recipe plus the pattern's signature input, `patternFab.json`).
 *
 * The file keeps a ware or good carry's tonnes in `inputs`; `recipeFor` splits it out, so `FabRecipe.inputs` holds the
 * ordinary inputs only and `carry`/`carryQty` the one carried item (the `FabOrder.required` / `FabOrder.carry` split).
 */
import { z } from 'zod'
import { isGoodId } from '../career/parts'
import type { FabCarryId, GoodOrWare, PatternId } from '../career/types'
import { resolveModule } from '../data/m2'
import {
  COMPONENT_TYPES,
  WARE_IDS,
  baseModuleId,
  type ComponentType,
  type FabMaterialsRaw,
  type FabMinBand,
  type FabWhere,
  parseModuleId,
  type M2Data,
  type ModuleId,
  type ModuleMk,
  type StationDef,
} from '../data/m2Schemas'
import patternFabJson from '../data/patternFab.json'
import { loadPatterns, type PatternsFile } from '../data/patterns'
import type { SystemEconomyView } from '../strategic/types'

/** A Mk II/III module's materials, ids checked. */
export interface FabRecipe {
  moduleId: ModuleId
  /** The share of the module's credit price paid at start. */
  creditShare: number
  /** The ordinary inputs, in tonnes (goods are 1 t a unit); the carry is not among them. */
  inputs: Partial<Record<GoodOrWare, number>>
  /** The one item you can't simply buy where you fabricate (plan decision 3). */
  carry: FabCarryId
  /** How much of the carry: tonnes of a ware or good, or 1 for a component (a data core). */
  carryQty: number
  /** A salvaged `component` at `minBand` or better stands in for `replaces` (an input or the carry). */
  salvageAlt?: { component: ComponentType; replaces: FabCarryId; minBand: FabMinBand }
  /** Where it can be fabricated (MC-c6's `offeredAt` reads it). */
  where: FabWhere
}

const M2_WARES: readonly string[] = WARE_IDS
const COMPONENTS: readonly string[] = COMPONENT_TYPES

/** A ware (M2 raw) or a good (`career/parts.ts GOOD_IDS`): what a recipe's `inputs` may name. */
export function isGoodOrWare(id: string): id is GoodOrWare {
  return M2_WARES.includes(id) || isGoodId(id)
}

export function isComponentType(id: string): id is ComponentType {
  return COMPONENTS.includes(id)
}

function recipeError(moduleId: ModuleId, path: string, id: string): Error {
  return new Error(`modules.json ${moduleId} materials.${path}: unknown ware, good or component "${id}"`)
}

function itemId(moduleId: ModuleId, path: string, id: string): FabCarryId {
  if (isGoodOrWare(id) || isComponentType(id)) return id
  throw recipeError(moduleId, path, id)
}

/** Converts one module's file block; throws naming the module and the id when an id is not a ware, good or component. */
export function toFabRecipe(moduleId: ModuleId, m: Readonly<FabMaterialsRaw>): FabRecipe {
  const carry = itemId(moduleId, 'carry', m.carry)
  const inputs: Partial<Record<GoodOrWare, number>> = {}
  let carryQty = isComponentType(carry) ? 1 : 0
  for (const [id, t] of Object.entries(m.inputs)) {
    if (!isGoodOrWare(id)) throw recipeError(moduleId, `inputs.${id}`, id)
    if (id === carry) carryQty = t
    else inputs[id] = t
  }
  const recipe: FabRecipe = { moduleId, creditShare: m.creditShare, inputs, carry, carryQty, where: m.where }
  if (m.salvageAlt) {
    const { component, replaces, minBand } = m.salvageAlt
    recipe.salvageAlt = { component, replaces: itemId(moduleId, 'salvageAlt.replaces', replaces), minBand }
  }
  return recipe
}

// ---------------------------------------------------------------------------------------------
// MC-f2c: pattern recipes (fabricate-only pattern modules, D-TBD T50)
// ---------------------------------------------------------------------------------------------

export const patternFabFileSchema = z.strictObject({
  /** Tonnes of a ware or good signature input a pattern recipe adds, per rung (a component signature is one whole part). */
  signatureT: z.strictObject({ mk2: z.number().positive(), mk3: z.number().positive() }),
})
export type PatternFabFile = z.infer<typeof patternFabFileSchema>

/** Parses `patternFab.json`; throws naming the bad path. */
export function loadPatternFab(raw: unknown = patternFabJson): PatternFabFile {
  return patternFabFileSchema.parse(raw)
}

/** What a pattern recipe reads: `patterns.json` (the signature input) and `patternFab.json` (how much). */
export interface PatternRecipeData {
  patterns: PatternsFile
  fab: PatternFabFile
}

let shippedPatternData: PatternRecipeData | null = null

/** The shipped `patterns.json` and `patternFab.json`, parsed once. */
export function patternRecipeData(): PatternRecipeData {
  shippedPatternData ??= { patterns: loadPatterns(), fab: loadPatternFab() }
  return shippedPatternData
}

/**
 * `base` (the base module's recipe) with `pattern`'s signature input added; `moduleId` is the pattern id.
 * - A ware or good: `fab.signatureT[mk]` more tonnes of it, as an ordinary input (or more of the carry, when the
 *   carry is that ware).
 * - A component (a data core, a radiator): one whole part. When the carry is that component, the carry needs one more;
 *   when the carry is a ware, the component becomes the carry (the one item you can't buy where you fabricate,
 *   decision 3) and the ware's tonnes become an ordinary input, so the salvage alternative still finds what it replaces.
 *   A recipe whose carry is another component throws: one carry per recipe (data test 2).
 */
function withSignature(base: FabRecipe, moduleId: ModuleId, mk: ModuleMk, pattern: PatternId, d: PatternRecipeData): FabRecipe {
  const def = d.patterns.patterns[pattern]
  if (def === undefined) throw new Error(`pattern ${pattern} has no patterns.json entry`)
  const sig = def.signatureInput
  const inputs = { ...base.inputs }
  if (isComponentType(sig)) {
    if (base.carry === sig) return { ...base, moduleId, inputs, carryQty: base.carryQty + 1 }
    if (isComponentType(base.carry)) {
      throw new Error(`pattern ${pattern}: ${moduleId} carries ${base.carry}, so the ${sig} signature has no carry to take`)
    }
    inputs[base.carry] = (inputs[base.carry] ?? 0) + base.carryQty
    return { ...base, moduleId, inputs, carry: sig, carryQty: 1 }
  }
  const t = d.fab.signatureT[mk === 3 ? 'mk3' : 'mk2']
  if (base.carry === sig) return { ...base, moduleId, inputs, carryQty: base.carryQty + t }
  inputs[sig] = (inputs[sig] ?? 0) + t
  return { ...base, moduleId, inputs }
}

/**
 * The recipe for `moduleId`, or null when it has none (Mk I, or a Mk II/III not yet given one). A pattern id
 * (`pdc.mk2.varro`, D-511) gets its base module's recipe plus the pattern's signature input (MC-f2c,
 * `withSignature`); its credit share is of the base price, which `resolveModule` keeps. Pure.
 */
export function recipeFor(data: Pick<M2Data, 'modules'>, moduleId: ModuleId, patterns?: PatternRecipeData): FabRecipe | null {
  const { mk, pattern } = parseModuleId(moduleId)
  if (pattern === undefined) {
    const m = resolveModule(data, moduleId).materials
    return m === undefined ? null : toFabRecipe(moduleId, m)
  }
  const base = recipeFor(data, baseModuleId(moduleId))
  return base === null ? null : withSignature(base, moduleId, mk, pattern, patterns ?? patternRecipeData())
}

/** Every recipe, in `MODULE_IDS` order (the data tests and the balance pass read the whole table). */
export function allRecipes(data: Pick<M2Data, 'modules'>): FabRecipe[] {
  const out: FabRecipe[] = []
  for (const def of data.modules.modules) {
    const r = recipeFor(data, def.id)
    if (r) out.push(r)
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// MC-c6: Mk III geography (plan decisions 4 and 5, D-TBD T43/T49)
// ---------------------------------------------------------------------------------------------

/** What `offeredAt` reads of a system: its stations' strategic template ids (`SystemEconomyView.stations`). */
export type FabSystem = { readonly stations: readonly Pick<SystemEconomyView['stations'][number], 'template'>[] }

/**
 * The `strategic/templates.json` ids whose presence lets a system fabricate Mk III (`where: 'fabworks'`): a fabworks
 * (`templates.json` `fabworks`; a player-owned fabworks builds the same template, decision 5) or a faction yard
 * (`yards`). Construction sites are not stations, so a fabworks still being built doesn't count.
 */
export const MK3_FAB_TEMPLATES: readonly string[] = ['fabworks', 'yards']

/** True when `system` has a fabworks (any owner, the player's too) or a faction yard; false for no view. Pure. */
export function systemFabricatesMk3(system: FabSystem | null): boolean {
  if (system === null) return false
  for (const s of system.stations) if (MK3_FAB_TEMPLATES.includes(s.template)) return true
  return false
}

/**
 * Whether `recipe` can be fabricated at `station` in `system`: the station outfits the module's kind, and a
 * `fabworks` recipe (every Mk III) also needs a fabworks or yard in the system (`systemFabricatesMk3`). A null
 * `system` (no strategic view) offers only `any` recipes. Player and AI read the same rule. Pure.
 */
export function offeredAt(recipe: FabRecipe, station: Pick<StationDef, 'services' | 'outfitting'>, system: FabSystem | null): boolean {
  if (!station.services.includes('outfitting')) return false
  if (!station.outfitting.includes(parseModuleId(recipe.moduleId).kind)) return false
  return recipe.where === 'any' || systemFabricatesMk3(system)
}
